import { fetchEventFieldMappings } from 'api/WorkflowsAPI';
import { AceEditor } from 'modules/aceEditor/AceEditorWrapper';
import { addNegativeToast } from 'modules/toastContainer';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PrimaryButton } from 'ui/button';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import { FormField } from 'ui/formField';
import { Add16 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { BasicSelect } from 'ui/selects';

import { createWorkflowWebhookRequest, updateWorkflowWebhookRequest } from '../actions';
import {
  AUTH_TYPES,
  dropdowns,
  FORM_URL_ENCODED_CONTENT_TYPE,
  INKIT_DATA,
  JSON_CONTENT_TYPE,
  NEW_WEBHOOK,
  SECURE_CREDENTIAL_VALUE,
  t,
} from '../constants';
import { transformUserFieldMappings } from '../utils';
import { ClientSecretInput } from './ClientSecretInput';
import { JsonUserField } from './JsonUserField';
import { KeyValuePair } from './KeyValuePair';

import {
  AceEditorWrapper,
  AddButton,
  AddButtonLabel,
  AuthenticationSelect,
  BasicAuthSection,
  BodySelect,
  CancelButton,
  Caption,
  CheckboxField,
  CollapsableGridRow,
  FormSection,
  Hint,
  Label,
  Monospace,
  OAuthSection,
  WorkflowWebhookForm,
} from './WorkflowWebhookEditor.styled';

import type {
  Errors,
  KeyValuePair as KVP,
  UserFieldOption,
  WebhookKeyValuePairKey,
  WebhookUserJsonField,
  WorkflowWebhook,
} from '../types';

import { EditMode } from '../types';

type Props = {
  mode: EditMode;
  webhook: WorkflowWebhook;
  onUpdate: (webhook: WorkflowWebhook) => void;
  onSave: (webhook: WorkflowWebhook) => void;
  onCancel?: () => void;
};

const VALID_WEBHOOK_DESTINATION = /http(s)?:\/\//;

export const WorkflowWebhookEditor = ({ mode, webhook, onUpdate, onSave, onCancel }: Props) => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<Errors>({});
  const [keyValueErrors, setKeyValueErrors] = useState<Record<string, boolean[]>>({
    headers: [],
    formBody: [],
  });

  const [userFieldOptions, setUserFieldOptions] = useState<UserFieldOption[]>([]);

  useEffect(() => {
    fetchEventFieldMappings().then((data) => {
      const userMappings: WebhookUserJsonField[] = data.mappings.user;
      if (userMappings) {
        setUserFieldOptions(transformUserFieldMappings(userMappings));
      }
    });
  }, []);

  const isCustomDestination = webhook.destination === 'Custom';

  const updateWebhook =
    (field: keyof WorkflowWebhook) =>
    (value: any): void => {
      onUpdate({
        ...webhook,
        [field]: value,
      });
    };

  const validateField = (field: string) => {
    switch (field) {
      case t.name.id:
        return !webhook.name;
      case t.endpoint.id:
        return !webhook.endpoint;
      case t.destination.id:
        return VALID_WEBHOOK_DESTINATION.test(webhook.destination);
      case t.method.id:
        return !webhook.method;
      case t.contentType.id:
        return !webhook.contentType;
      case t.authType.id:
        return !webhook.authType;
      case t.basicAuthToken.id:
        return webhook.authType === AUTH_TYPES.Basic && !webhook.basicAuthToken;
      case t.headers.id:
        return (
          webhook.authType === AUTH_TYPES.Basic &&
          !!webhook.modifiedCustomHeaders &&
          webhook.headers.length > 0 &&
          webhook.basicAuthToken.includes(SECURE_CREDENTIAL_VALUE)
        );
      case t.jsBody.id: {
        try {
          // Attempt to parse the JSON body if one exists
          JSON.parse(webhook.jsBody || '{}');
          return false;
        } catch {
          return true;
        }
      }
      default:
        return false;
    }
  };

  const validate = (field: string) => () => {
    setErrors({
      ...errors,
      [field]: validateField(field),
    });
  };

  const validateKeyValueField = (field: 'formBody' | 'headers') => () => {
    setKeyValueErrors({
      ...keyValueErrors,
      [field]: webhook[field].map(({ key }) => !key),
    });
  };

  const validateForm = () => {
    const errorsFromForm = Object.keys(webhook).reduce(
      (err: Record<string, boolean>, field: string) => {
        return { ...err, [field]: validateField(field) };
      },
      {},
    );

    setErrors(errorsFromForm);
    setKeyValueErrors({
      [t.headers.id]: webhook.headers.map(({ key }) => !key),
      [t.formBody.id]: webhook.formBody.map(({ key }) => !key),
    });

    const hasHeaderError = webhook.headers.find((valid) => !valid.key);
    const hasBodyError = webhook.formBody.find((valid) => !valid.key);
    const hasErrorsFromForm = Object.values(errorsFromForm).find((valid) => valid);
    return !(hasHeaderError || hasBodyError || hasErrorsFromForm);
  };

  const addKeyValuePair = (type: WebhookKeyValuePairKey) => (): void => {
    const keyValuePairs = webhook[type].concat({ key: '', value: '' });
    updateWebhook(type)(keyValuePairs);
  };

  const updateKeyValuePair =
    (type: WebhookKeyValuePairKey, index: number) =>
    (updatedKeyValuePair: KVP): void => {
      const keyValuePairs = webhook[type].map((originalKeyValuePair, idx) => {
        return idx === index ? updatedKeyValuePair : originalKeyValuePair;
      });

      onUpdate({
        ...webhook,
        [type]: keyValuePairs,
        modifiedCustomHeaders: true,
      });
    };

  const deleteKeyValuePair = (type: WebhookKeyValuePairKey, index: number) => (): void => {
    const keyValuePairs = webhook[type].filter((kvp, idx) => {
      return idx !== index;
    });
    setKeyValueErrors({
      ...keyValueErrors,
      [type]: keyValuePairs.map((_, idx) => validateField(`${type}-${idx}`)),
    });

    onUpdate({
      ...webhook,
      [type]: keyValuePairs,
      modifiedCustomHeaders: true,
    });
  };

  const addUserField = () => {
    const userFields = webhook.userFields.concat({ field: '' });
    updateWebhook(t.userFields.id)(userFields);
  };

  const updateUserField =
    (index: number) =>
    (updatedUserField: WebhookUserJsonField): void => {
      const userFields = webhook.userFields.map((originalField, idx) => {
        /* @ts-expect-error */
        return idx === index ? { field: updatedUserField.value } : originalField;
      });
      updateWebhook(t.userFields.id)(userFields);
    };

  const deleteUserField = (index: number) => (): void => {
    const userFields = webhook.userFields.map((field, idx) => {
      return idx !== index ? field : { ...field, deleted: true };
    });
    updateWebhook(t.userFields.id)(userFields);
  };

  const saveWebhook = () => {
    if (!validateForm()) {
      dispatch(addNegativeToast('Cannot save webhook, please correct any errors and try again.'));
    } else if (mode === EditMode.Create) {
      dispatch(createWorkflowWebhookRequest(webhook, onSave));
    } else if (mode === EditMode.EditName || mode === EditMode.EditWebhook) {
      dispatch(updateWorkflowWebhookRequest(webhook, onSave));
    }
  };

  return (
    <WorkflowWebhookForm>
      <CollapsableGridRow collapsed={mode !== EditMode.EditName && mode !== EditMode.Create}>
        <FormField
          errorText={t.name.errorText}
          inputId={t.name.id}
          labelText={t.name.label}
          hasError={errors.name}
        >
          <InputText
            type="text"
            id={t.name.id}
            hasError={errors.name}
            isRequired
            onChange={updateWebhook(t.name.id)}
            value={webhook.name}
            onBlur={validate(t.name.id)}
          />
        </FormField>
      </CollapsableGridRow>
      {(mode === EditMode.EditWebhook || mode === EditMode.Create) && (
        <>
          <FormField
            inputId={t.destination.id}
            labelText={t.destination.label}
            hasError={errors.destination}
          >
            <BasicSelect
              id="workflow-webhook-destination"
              defaultValues={[webhook.destination]}
              onSelect={([val]) => {
                if (val === 'Inkit') {
                  onUpdate({
                    ...INKIT_DATA,
                    name: webhook.name,
                  });
                } else {
                  onUpdate({
                    ...NEW_WEBHOOK,
                    name: webhook.name,
                  });
                }
              }}
              options={dropdowns.destination}
              isFullWidth
            />
          </FormField>

          <FormField inputId={t.method.id} labelText={t.method.label} hasError={errors.method}>
            <BasicSelect
              id="workflow-webhook-method"
              isDisabled={!isCustomDestination}
              defaultValues={[webhook.method]}
              onSelect={([val]) => updateWebhook(t.method.id)(val)}
              options={dropdowns.method}
              isFullWidth
            />
          </FormField>

          <FormField
            errorText={t.endpoint.errorText}
            helpText={t.endpoint.helpText}
            inputId={t.endpoint.id}
            labelText={t.endpoint.label}
            hasError={errors.endpoint}
          >
            <InputText
              type="text"
              id={t.endpoint.id}
              hasError={errors.endpoint}
              isRequired
              isDisabled={!isCustomDestination}
              onChange={updateWebhook(t.endpoint.id)}
              value={webhook.endpoint}
              onBlur={validate(t.endpoint.id)}
            />
          </FormField>

          <FormSection>
            <FormField hasError={false} inputId={t.authType.id} labelText={t.authType.label}>
              <AuthenticationSelect
                id="workflow-webhook-auth-type"
                defaultValues={[webhook.authType]}
                isDisabled={!isCustomDestination}
                onSelect={([val]) => updateWebhook(t.authType.id)(val)}
                options={dropdowns.authType}
              />
            </FormField>

            {webhook.authType === 'Basic' && (
              <BasicAuthSection>
                <FormField
                  hasError={errors.basicAuthToken || errors.headers}
                  errorText={errors.headers ? t.headers.basicAuthErrorText : ''}
                  helpText={mode === EditMode.Create ? t.basicAuthToken.helpText : ''}
                  inputId={t.basicAuthToken.id}
                  labelText={t.basicAuthToken.label}
                >
                  <ClientSecretInput
                    id={t.basicAuthToken.id}
                    hasError={errors.basicAuthToken || errors.headers}
                    isRequired
                    onChange={(basicAuthToken) => {
                      onUpdate({
                        ...webhook,
                        basicAuthToken,
                      });
                    }}
                    onCleared={() => {
                      onUpdate({
                        ...webhook,
                        basicAuthToken: '',
                      });
                    }}
                    value={webhook.basicAuthToken || ''}
                  />
                </FormField>
              </BasicAuthSection>
            )}

            {webhook.authType === 'OAuth2' && (
              <OAuthSection>
                <FormField hasError={false} inputId={t.clientId.id} labelText={t.clientId.label}>
                  <InputText
                    type="text"
                    id={t.clientId.id}
                    hasError={errors.clientId}
                    isRequired
                    onChange={updateWebhook(t.clientId.id)}
                    value={webhook.clientId}
                  />
                </FormField>
                <FormField
                  hasError={false}
                  helpText={mode === EditMode.Create ? t.clientSecret.helpText : ''}
                  inputId={t.clientSecret.id}
                  labelText={t.clientSecret.label}
                >
                  <ClientSecretInput
                    id={t.clientSecret.id}
                    hasError={errors.clientSecret}
                    isRequired
                    onChange={(clientSecret) => {
                      onUpdate({
                        ...webhook,
                        modifiedClientSecret: true,
                        clientSecret,
                      });
                    }}
                    onCleared={() => {
                      onUpdate({
                        ...webhook,
                        modifiedClientSecret: true,
                        clientSecret: '',
                      });
                    }}
                    value={webhook.clientSecret || ''}
                  />
                </FormField>
                <FormField
                  hasError={false}
                  inputId={t.tokenEndpoint.id}
                  labelText={t.tokenEndpoint.label}
                >
                  <InputText
                    type="text"
                    id={t.tokenEndpoint.id}
                    hasError={errors.tokenEndpoint}
                    isRequired
                    onChange={updateWebhook(t.tokenEndpoint.id)}
                    value={webhook.tokenEndpoint}
                  />
                </FormField>
              </OAuthSection>
            )}
          </FormSection>

          <FormSection>
            <Label isRequired={false}>
              {t.headers.label} <Caption>{t.headers.subLabel}</Caption>
            </Label>
            <Hint>{t.headers.hint}</Hint>
            {webhook.headers &&
              webhook.headers.map((header, idx) => (
                <KeyValuePair
                  showHeader={idx === 0}
                  keyReadOnly={!isCustomDestination}
                  key={`headers-${idx}`}
                  k={header.key}
                  v={header.value}
                  hasError={!!keyValueErrors[t.headers.id][idx]}
                  onBlurKey={validateKeyValueField(t.headers.id)}
                  onChange={updateKeyValuePair(t.headers.id, idx)}
                  onDelete={deleteKeyValuePair(t.headers.id, idx)}
                />
              ))}
            {isCustomDestination && (
              <AddButton data-test="add-header-button" onClick={addKeyValuePair(t.headers.id)}>
                <Add16 />
                <AddButtonLabel>Add header</AddButtonLabel>
              </AddButton>
            )}
          </FormSection>

          <FormSection>
            <FormField hasError={false} inputId={t.contentType.id} labelText={t.contentType.label}>
              <BodySelect
                id="workflow-webhook-content-type"
                defaultValues={[webhook.contentType]}
                isDisabled={!isCustomDestination}
                onSelect={([val]) => updateWebhook(t.contentType.id)(val)}
                options={dropdowns.contentType}
              />
            </FormField>
            {webhook.contentType === JSON_CONTENT_TYPE && (
              <AceEditorWrapper>
                <AceEditor
                  height="256px"
                  mode="json"
                  onChange={(val) => updateWebhook(t.jsBody.id)(val)}
                  showGutter={false}
                  showPrintMargin={false}
                  value={webhook.jsBody}
                  wrapEnabled
                />
              </AceEditorWrapper>
            )}
            {webhook.contentType === FORM_URL_ENCODED_CONTENT_TYPE && (
              <>
                {webhook.formBody.map((kvp, idx) => (
                  <KeyValuePair
                    showHeader={idx === 0}
                    keyReadOnly={!isCustomDestination}
                    key={`form-body-${idx}`}
                    k={kvp.key}
                    v={kvp.value}
                    canDelete
                    hasError={!!keyValueErrors[t.formBody.id][idx]}
                    onBlurKey={validateKeyValueField(t.formBody.id)}
                    onChange={updateKeyValuePair(t.formBody.id, idx)}
                    onDelete={deleteKeyValuePair(t.formBody.id, idx)}
                  />
                ))}
                {isCustomDestination && (
                  <AddButton
                    data-test="add-form-field-button"
                    onClick={addKeyValuePair(t.formBody.id)}
                  >
                    <Add16 />
                    <AddButtonLabel>Add form field</AddButtonLabel>
                  </AddButton>
                )}
              </>
            )}
            <CheckboxField>
              <Checkbox
                status={
                  webhook.includeDefaultFields ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED
                }
                labelId="incpude-default-fields-label"
                name="includeDefaultFields"
                onClick={() => updateWebhook('includeDefaultFields')(!webhook.includeDefaultFields)}
              />
              <span
                onClick={() => updateWebhook('includeDefaultFields')(!webhook.includeDefaultFields)}
              >
                Include <Monospace>email</Monospace> and <Monospace>workflowId</Monospace>
              </span>
            </CheckboxField>
            {webhook.userFields.map((field, idx) =>
              field.deleted ? null : (
                <JsonUserField
                  id={`user-field-${idx}`}
                  key={`user-field-${idx}`}
                  body={field}
                  editable={true}
                  canDelete={true}
                  options={userFieldOptions}
                  onChange={updateUserField(idx)}
                  onDelete={deleteUserField(idx)}
                />
              ),
            )}
            {webhook.contentType === JSON_CONTENT_TYPE && (
              <AddButton data-test="add-user-field" onClick={addUserField}>
                <Add16 />
                <AddButtonLabel>Add user field</AddButtonLabel>
              </AddButton>
            )}
          </FormSection>
        </>
      )}
      <FormSection>
        <PrimaryButton onClick={saveWebhook}>
          {mode === EditMode.Create ? t.createButton : t.saveButton}
        </PrimaryButton>
        {mode !== EditMode.Create && (
          <CancelButton onClick={() => onCancel?.()}>{t.cancelButton}</CancelButton>
        )}
      </FormSection>
    </WorkflowWebhookForm>
  );
};
